import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { 
  TrendingUp, 
  DollarSign, 
  Users, 
  Building2, 
  Download, 
  Calendar,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

const AdminAnalytics = () => {
  const { getToken } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    loadAdminAnalytics();
  }, [dateRange]);

  const loadAdminAnalytics = async () => {
    try {
      const token = await getToken();
      
      const response = await fetch(`${API_URL}/api/admin/analytics?days=${dateRange}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      setAnalytics(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setLoading(false);
    }
  };

  const exportReport = async () => {
    try {
      const token = await getToken();
      
      const response = await fetch(`${API_URL}/api/admin/analytics/export?days=${dateRange}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `admin-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error('Error exporting report:', error);
    }
  };

  if (loading) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '40px',
        textAlign: 'center'
      }}>
        <p style={{ color: '#6b7280' }}>Loading admin analytics...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
              Admin Dashboard - All Facilities
            </h2>
            <p style={{ color: '#6b7280', fontSize: '14px' }}>
              StorageScout platform-wide analytics
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              style={{
                padding: '10px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
            
            <button
              onClick={exportReport}
              style={{
                background: '#10b981',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Download size={16} />
              Export Report
            </button>
          </div>
        </div>
      </div>

      {/* Platform-Wide Metrics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '20px',
        marginBottom: '24px'
      }}>
        <MetricCard
          icon={<Building2 size={24} color="#667eea" />}
          title="Total Facilities"
          value={analytics?.total_facilities || 0}
          change={analytics?.facilities_change || 0}
          color="#667eea"
        />
        <MetricCard
          icon={<Users size={24} color="#10b981" />}
          title="Total Bookings"
          value={analytics?.total_bookings || 0}
          change={analytics?.bookings_change || 0}
          color="#10b981"
        />
        <MetricCard
          icon={<DollarSign size={24} color="#f59e0b" />}
          title="Total Revenue"
          value={`$${analytics?.total_revenue?.toLocaleString() || 0}`}
          change={analytics?.revenue_change || 0}
          color="#f59e0b"
        />
        <MetricCard
          icon={<TrendingUp size={24} color="#3b82f6" />}
          title="Avg. Revenue/Facility"
          value={`$${analytics?.avg_revenue_per_facility || 0}`}
          change={analytics?.avg_revenue_change || 0}
          color="#3b82f6"
        />
      </div>

      {/* Revenue Timeline */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px' }}>
          Revenue Over Time
        </h3>
        <RevenueChart data={analytics?.revenue_timeline || []} />
      </div>

      {/* Facility Performance Table */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px' }}>
          Facility Performance
        </h3>
        <FacilityPerformanceTable facilities={analytics?.facility_performance || []} />
      </div>

      {/* Recent Activity */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px' }}>
          Recent Bookings - All Facilities
        </h3>
        <RecentBookingsTable bookings={analytics?.recent_bookings || []} />
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ icon, title, value, change, color }) => (
  <div style={{
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
      <div style={{
        width: '48px',
        height: '48px',
        background: `${color}15`,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {icon}
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '14px',
        fontWeight: '600',
        color: change >= 0 ? '#10b981' : '#ef4444'
      }}>
        {change >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
        {Math.abs(change)}%
      </div>
    </div>
    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
      {title}
    </div>
    <div style={{ fontSize: '28px', fontWeight: '800', color: '#1a1a1a' }}>
      {value}
    </div>
  </div>
);

// Revenue Chart
const RevenueChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <p style={{ color: '#9ca3af', textAlign: 'center' }}>No data available</p>;
  }

  const maxValue = Math.max(...data.map(d => d.value));

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', height: '250px' }}>
      {data.map((item, idx) => (
        <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#10b981', marginBottom: '8px' }}>
            ${item.value}
          </div>
          <div
            style={{
              width: '100%',
              height: `${(item.value / maxValue) * 200}px`,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '4px 4px 0 0',
              transition: 'height 0.3s',
              minHeight: '4px'
            }}
            title={`$${item.value}`}
          />
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
            {item.label}
          </div>
        </div>
      ))}
    </div>
  );
};

// Facility Performance Table
const FacilityPerformanceTable = ({ facilities }) => {
  if (!facilities || facilities.length === 0) {
    return <p style={{ color: '#9ca3af', textAlign: 'center' }}>No facilities found</p>;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f9fafb' }}>
            <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
              Facility
            </th>
            <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
              City
            </th>
            <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
              Bookings
            </th>
            <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
              Revenue
            </th>
            <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
              Occupancy
            </th>
            <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
              Avg. Booking
            </th>
          </tr>
        </thead>
        <tbody>
          {facilities.map((facility, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600' }}>
                {facility.name}
              </td>
              <td style={{ padding: '12px', fontSize: '14px' }}>
                {facility.city}
              </td>
              <td style={{ padding: '12px', fontSize: '14px', textAlign: 'right' }}>
                {facility.bookings}
              </td>
              <td style={{ padding: '12px', fontSize: '14px', fontWeight: '700', color: '#10b981', textAlign: 'right' }}>
                ${facility.revenue}
              </td>
              <td style={{ padding: '12px', fontSize: '14px', textAlign: 'right' }}>
                <span style={{
                  background: facility.occupancy >= 80 ? '#d1fae5' : facility.occupancy >= 60 ? '#fef3c7' : '#fee2e2',
                  color: facility.occupancy >= 80 ? '#065f46' : facility.occupancy >= 60 ? '#92400e' : '#991b1b',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600'
                }}>
                  {facility.occupancy}%
                </span>
              </td>
              <td style={{ padding: '12px', fontSize: '14px', textAlign: 'right' }}>
                ${facility.avg_booking}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Recent Bookings Table
const RecentBookingsTable = ({ bookings }) => {
  if (!bookings || bookings.length === 0) {
    return <p style={{ color: '#9ca3af', textAlign: 'center' }}>No recent bookings</p>;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f9fafb' }}>
            <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
              Date
            </th>
            <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
              Facility
            </th>
            <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
              Customer
            </th>
            <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
              Storage Type
            </th>
            <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
              Duration
            </th>
            <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
              Referral Fee
            </th>
          </tr>
        </thead>
        <tbody>
          {bookings.map((booking, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '12px', fontSize: '14px' }}>
                {new Date(booking.date).toLocaleDateString()}
              </td>
              <td style={{ padding: '12px', fontSize: '14px' }}>
                {booking.facility_name}
              </td>
              <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600' }}>
                {booking.customer_name}
              </td>
              <td style={{ padding: '12px', fontSize: '14px' }}>
                {booking.storage_type}
              </td>
              <td style={{ padding: '12px', fontSize: '14px', textAlign: 'right' }}>
                {booking.duration_months} months
              </td>
              <td style={{ padding: '12px', fontSize: '14px', fontWeight: '700', color: '#10b981', textAlign: 'right' }}>
                ${booking.referral_fee}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminAnalytics;
